import React, { useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import TestAgencyPill from '../TestAgencyPill/TestAgencyPill'
import styles from './AgencyCombobox.module.scss'

export type TAgencyComboOption = {
  agencyId: string
  /** Trading name, or the entity name when there is none. */
  title: string
  /** Entity name, only when it differs from the title. */
  subtitle: string | null
  isTest: boolean
}

type TProps = {
  options: TAgencyComboOption[]
  value: TAgencyComboOption | null
  onChange: (option: TAgencyComboOption | null) => void
  placeholder?: string
}

// Rendering thousands of rows makes typing lag; search narrows it down.
const MAX_ROWS = 200

/**
 * Searchable agency picker modelled on Eventy's Statements page: a plain
 * input, a list of two-line rows (trading name over entity name), arrow keys
 * and Enter to pick, × to clear.
 */
const AgencyCombobox = ({ options, value, onChange, placeholder = 'Search agencies' }: TProps) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const listRef = useRef<HTMLUListElement>(null)

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase()
    const hits = term
      ? options.filter(o => `${o.title} ${o.subtitle ?? ''} ${o.agencyId}`.toLowerCase().includes(term))
      : options
    return hits.slice(0, MAX_ROWS)
  }, [options, query])

  // Keep the highlighted row in view while arrowing through the list.
  useEffect(() => {
    if (highlight < 0) return
    const row = listRef.current?.children[highlight] as HTMLElement | undefined
    row?.scrollIntoView({ block: 'nearest' })
  }, [highlight])

  const pick = (option: TAgencyComboOption) => {
    onChange(option)
    setQuery('')
    setOpen(false)
    setHighlight(-1)
  }

  const clear = () => {
    onChange(null)
    setQuery('')
    setHighlight(-1)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setHighlight(h => Math.min(h + 1, matches.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight(h => Math.max(h - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const target = matches[highlight] ?? (matches.length === 1 ? matches[0] : undefined)
      if (target) pick(target)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  // While closed the field shows the picked agency; typing searches.
  const inputValue = open || query ? query : value?.title ?? ''

  return (
    <div className={styles.root}>
      <div className={styles.field}>
        <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          className={styles.input}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={value ? value.title : placeholder}
          value={inputValue}
          onChange={event => {
            setQuery(event.target.value)
            setOpen(true)
            setHighlight(-1)
          }}
          onFocus={() => setOpen(true)}
          // Deferred so a click on a row lands before the list closes.
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
        />
        {value && (
          <button type="button" className={styles.clear} onClick={clear} tabIndex={-1} aria-label="Clear agency">
            ×
          </button>
        )}
      </div>

      {open && (
        <ul ref={listRef} className={styles.list} role="listbox">
          {matches.length === 0 ? (
            <li className={styles.empty}>{options.length === 0 ? 'No agencies loaded' : 'No agencies match'}</li>
          ) : (
            matches.map((option, index) => (
              <li
                key={option.agencyId}
                role="option"
                aria-selected={option.agencyId === value?.agencyId}
                className={cx(styles.row, {
                  [styles.highlighted]: index === highlight,
                  [styles.selected]: option.agencyId === value?.agencyId,
                })}
                // mousedown fires before the input's blur; preventDefault keeps focus.
                onMouseDown={event => {
                  event.preventDefault()
                  pick(option)
                }}
                onMouseEnter={() => setHighlight(index)}
              >
                <span className={styles.title}>
                  <span className={styles['title-text']}>{option.title}</span>
                  {option.isTest && <TestAgencyPill />}
                </span>
                {option.subtitle && <span className={styles.subtitle}>{option.subtitle}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

export default AgencyCombobox
